import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
} from "react";
import { USER_TYPE } from "utils/dataCenter";
import {
  CognitoUser,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  IAuthenticationCallback,
} from "amazon-cognito-identity-js";
// utils
import axios from "../apis/axios";
import { getPhoneNumberString } from "../utils/regexUtil";
// routes
import { PATH_AUTH } from "../routes/paths";
// @types
import {
  ActionMap,
  AuthState,
  AuthUser,
  AWSCognitoContextType,
} from "../@types/authentication";
//
import { cognitoConfig } from "../config";
import * as apis from "../apis";
import * as loginAPI from "../logics/auth/login";
import * as reduxStore from "../redux/store";
import { setClear } from "../redux/slices/auth";
import Cookies from "js-cookie";
import { API_ERROR_CODE } from "apis/utils/dataCenter";

// ----------------------------------------------------------------------

// CAUTION: User Cognito is slily difference from firebase, so be sure to read the doc carefully.

export const UserPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId || "",
  ClientId: cognitoConfig.clientId || "",
});

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  navigateRoot: "",
};

enum Types {
  auth = "AUTHENTICATE",
  logout = "LOGOUT",
}

type AwsAuthPayload = {
  [Types.auth]: {
    isAuthenticated: boolean;
    user: AuthUser;
    navigateRoot: string;
  };
  [Types.logout]: undefined;
};

type AwsActions = ActionMap<AwsAuthPayload>[keyof ActionMap<AwsAuthPayload>];

const reducer = (state: AuthState, action: AwsActions) => {
  if (action.type === "AUTHENTICATE") {
    const { isAuthenticated, user, navigateRoot } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      navigateRoot,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
      navigateRoot: "",
    };
  }
  return state;
};

const AuthContext = createContext<AWSCognitoContextType | null>(null);

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getUserAttributes = useCallback(
    (currentUser: CognitoUser): Record<string, any> =>
      new Promise((resolve, reject) => {
        currentUser.getUserAttributes((err, attributes) => {
          if (err) {
            reject(err);
          } else {
            const results: Record<string, any> = {};

            attributes?.forEach((attribute) => {
              results[attribute.Name] = attribute.Value;
            });
            results.username = currentUser.getUsername().toLowerCase();
            resolve(results);
          }
        });
      }),
    []
  );

  const getSession = useCallback(
    () =>
      new Promise((resolve, reject) => {
        const user = UserPool.getCurrentUser();
        if (user) {
          user.getSession(
            async (err: Error | null, session: CognitoUserSession | null) => {
              if (err) {
                console.log("getSession error : ", err);
                reject(err);
              } else {
                try {
                  console.log("user : ", user);
                  const attributes = await getUserAttributes(user);

                  const token = session?.getAccessToken().getJwtToken();
                  const expireTime = session?.getAccessToken().getExpiration();
                  const refreshToken = session?.getRefreshToken().getToken();
                  console.log("expireTime : ", expireTime);
                  if (token != null) {
                    localStorage.setItem("tooderAccessToken", token);
                  }
                  if (refreshToken != null) {
                    Cookies.set("refreshToken", refreshToken, { expires: 1 });
                  }
                  console.log("session : ", session);
                  if (expireTime != null) {
                    localStorage.setItem(
                      "tokenExpTime",
                      String(expireTime * 1000)
                    );
                  }

                  // use the token or Bearer depend on the wait BE handle, by default amplify API only need to token.
                  axios.defaults.headers.common.Authorization = token;
                  console.log("attributes: ", attributes);

                  if (attributes !== null) {
                    if (attributes.phone_number_verified === "true") {
                      try {
                        let res: any = await loginAPI.loginProcess(attributes);
                        console.log("loginProcess success!!!", res);

                        dispatch({
                          type: Types.auth,
                          payload: {
                            isAuthenticated: true,
                            user: attributes,
                            navigateRoot: res.USER_TYPE,
                          },
                        });
                      } catch (e: any) {
                        console.log("loginProcess error : ", e);
                        switch (e.code) {
                          case API_ERROR_CODE.PERMISSION_DENIED:
                            throw e;
                            break;
                          case -21:
                            dispatch({
                              type: Types.auth,
                              payload: {
                                isAuthenticated: true,
                                user: attributes,
                                navigateRoot: `REGISTER_${attributes["custom:user_type"]}`,
                              },
                            });
                            break;

                          default:
                            dispatch({
                              type: Types.auth,
                              payload: {
                                isAuthenticated: true,
                                user: attributes,
                                navigateRoot: "",
                              },
                            });
                            break;
                        }
                      }
                    } else if (attributes.username != null) {
                      dispatch({
                        type: Types.auth,
                        payload: {
                          isAuthenticated: true,
                          user: attributes,
                          navigateRoot: `VERIFY_PHONE_NUMBER`,
                        },
                      });
                    } else {
                      dispatch({
                        type: Types.auth,
                        payload: {
                          isAuthenticated: false,
                          user: null,
                          navigateRoot: "",
                        },
                      });
                    }
                  } else {
                    dispatch({
                      type: Types.auth,
                      payload: {
                        isAuthenticated: false,
                        user: null,
                        navigateRoot: "",
                      },
                    });
                  }

                  resolve({
                    user,
                    session,
                    headers: { Authorization: token },
                  });
                } catch (e) {
                  console.log("e : ", e);
                  user.signOut();
                  reject(e);
                }
              }
            }
          );
        } else {
          dispatch({
            type: Types.auth,
            payload: {
              isAuthenticated: false,
              user: null,
              navigateRoot: "",
            },
          });
        }
      }),
    [getUserAttributes]
  );

  const initial = useCallback(async () => {
    try {
            dispatch({
                type: Types.auth,
                payload: {
                    isAuthenticated: false,
                    user: null,
                    navigateRoot: '',
                },
            });
            await logout();
    } catch (e: any) {
      console.log("initial error : ", e);
      dispatch({
        type: Types.auth,
        payload: {
          isAuthenticated: false,
          user: null,
                    navigateRoot: '',
        },
      });
    }
  }, [getSession]);

  useEffect(() => {
    initial();
  }, [initial]);

  // We make sure to handle the user update here, but return the resolve value in order for our components to be
  // able to chain additional `.then()` logic. Additionally, we `.catch` the error and "enhance it" by providing
  // a message that our React components can use.
  const login = useCallback(
    (username: string, password: any, remember: boolean) =>
      new Promise((resolve, reject) => {
        const lowerCase = username.toLowerCase();
        const user = new CognitoUser({
          Username: lowerCase,
          Pool: UserPool,
        });

        const authDetails = new AuthenticationDetails({
          Username: lowerCase,
          Password: password,
        });
        var authenticHandler: IAuthenticationCallback = {
          onSuccess: async (data: any) => {
            if (remember) {
              localStorage.setItem("username", username);
            } else {
              localStorage.setItem("username", "");
            }
            try {
              await getSession();
              console.log("data : ", data);
              resolve(data);
            } catch (e) {
              reject(e);
            }
          },
          onFailure: (err: any) => {
            console.log("err o :", err);
            if (err.code == "UserNotConfirmedException") {
              resendRegisterCode(lowerCase);
              window.location.href = `${PATH_AUTH.verify}?username=${lowerCase}`;
              return;
            }
            if (
              err.code == "UserNotFoundException" ||
              err.code == "NotAuthorizedException"
            ) {
              err.message =
                "아이디 또는 비밀번호가 잘못 입력 되었습니다. 아이디와 비밀번호를 정확히 입력해 주세요.";
            }
            reject(err);
          },
          newPasswordRequired: (
            userAttributes: any,
            requiredAttributes: any
          ) => {
            delete userAttributes["phone_number"];
            delete userAttributes["phone_number_verified"];
            user.completeNewPasswordChallenge(
              password,
              userAttributes,
              authenticHandler
            );
            resolve({ message: "newPasswordRequired" });
          },
        };
        user.authenticateUser(authDetails, authenticHandler);
      }),
    [getSession]
  );

  const refreshSession = () => {
    return new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();

      if (user) {
        const userSession = user.getSignInUserSession();
        if (userSession) {
          user.refreshSession(userSession.getRefreshToken(), (err, session) => {
            console.log("refresh Session err : ", err, session);
            // const { idToken, refreshToken, accessToken } = session;
            resolve(null);
          });
        } else {
          resolve(null);
        }
      } else {
        resolve(null);
      }
    });
  };

  // same thing here
    const logout = () => {
    return new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser();
      if (user) {
        user.signOut(() => {
          dispatch({ type: Types.logout });
          localStorage.removeItem("tooderAccessToken");
          localStorage.removeItem("tokenExpTime");
          Cookies.remove("refreshToken");
          reduxStore.dispatch(setClear());
          axios.defaults.headers.common.Authorization = null;
          console.log("axios2 : ", JSON.stringify(axios.defaults.headers));
          resolve(null);
        });
      } else {
        localStorage.removeItem("tooderAccessToken");
        localStorage.removeItem("tokenExpTime");
        Cookies.remove("refreshToken");
        reduxStore.dispatch(setClear());
        axios.defaults.headers.common.Authorization = null;
        resolve(null);
      }
    });
  };

  const refreshLoginProcess = (user: AuthUser) => {
    getSession();
  };

  const register = (
    username: string,
    password: string,
    phone_number: string,
    type: string,
    name: string,
    email: string
  ) =>
    new Promise((resolve, reject) => {
      const lowerCase = username.toLowerCase();
      // console.log("type : ", type)
      UserPool.signUp(
        lowerCase,
        password,
        [
          new CognitoUserAttribute({
            Name: "phone_number",
            Value: phone_number,
          }),
          new CognitoUserAttribute({ Name: "custom:user_type", Value: type }),
          new CognitoUserAttribute({ Name: "name", Value: name }),
          new CognitoUserAttribute({ Name: "email", Value: email }),
        ],
        [],
        async (err: any) => {
          console.log("register err: ", err);
          if (err) {
            if (err.code == "UsernameExistsException") {
              err.message =
                "이미 등록된 아이디입니다. 다른 아이디를 사용해주세요.";
            }
            reject(err);
            return;
          }
          resolve(undefined);
          window.location.href = `${PATH_AUTH.verify}?username=${lowerCase}`;
        }
      );
    });

  const confirmRegister = (username: string, code: string, userObj: any) =>
    new Promise((resolve, reject) => {
      let lowerCase = null;
      if (username == "") {
        lowerCase = userObj.username.toLowerCase();
      } else {
        lowerCase = username.toLowerCase();
      }
      console.log("lowerCase : ", lowerCase);
      const user = new CognitoUser({
        Username: lowerCase,
        Pool: UserPool,
      });
      user.confirmRegistration(code, false, async (err) => {
        // err.code "CodeMismatchException" checkcode 틀릴 때
        if (err) {
          reject(err);
          return;
        }
        resolve(undefined);
      });
    });

  const resendRegisterCode = (username: string) =>
    new Promise((resolve, reject) => {
      const lowerCase = username.toLowerCase();
      const user = new CognitoUser({
        Username: lowerCase,
        Pool: UserPool,
      });
      user.resendConfirmationCode((err, result) => {
        console.log("resendSignUp error : ", err);
        console.log("resendSignUp error : ", result);
        // err.code "InvalidParameterException" err.message "User is already confirmed."
        // err.code "UserNotFoundException" err.message "Username/client id combination not found."
        if (err) {
          reject(err);
          return;
        }
        resolve(undefined);
      });
    });

  const resetPassword = (username: string) =>
    new Promise((resolve, reject) => {
      const lowerCase = username.toLowerCase();
      const user = new CognitoUser({
        Username: lowerCase,
        Pool: UserPool,
      });

      console.log("user : ", user.getUsername());
      user.forgotPassword({
        onSuccess: (data) => {
          console.log("resetPassword data : ", data);
          resolve(data);
        },
        onFailure: (err: any) => {
          console.log("resetPassword error : ", err);
          // err.code "UserNotFoundException" err.message "Username/client id combination not found."
          if (err.code == "UserNotFoundException") {
            err.message = "아이디를 확인해주세요.";
          }
          reject(err);
        },
      });
    });

  const resetPasswordSubmit = (
    username: string,
    password: string,
    code: string
  ) =>
    new Promise((resolve, reject) => {
      const lowerCase = username.toLowerCase();
      const user = new CognitoUser({
        Username: lowerCase,
        Pool: UserPool,
      });
      user.confirmPassword(code, password, {
        onSuccess: () => {
          resolve(undefined);
          refreshLoginProcess(user);
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });

  const updateAttribute = (phone_number: string) =>
    new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser()!;
      user.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err) {
            reject(err);
            return;
          }
          console.log("getCurrentUser: ", user);
          user.updateAttributes(
            [
              new CognitoUserAttribute({
                Name: "phone_number",
                Value: phone_number,
              }),
            ],
            (err) => {
              if (err) {
                console.log("err: ", err);
                reject(err);
              }
              resolve(undefined);
            }
          );
        }
      );
    });

  const getAttributeVerificationCode = () =>
    new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser()!;
      user.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err) {
            reject(err);
            return;
          }
          user.getAttributeVerificationCode("phone_number", {
            onSuccess: () => {
              resolve(undefined);
            },
            onFailure: (err: Error) => {
              console.log("err: ", err);
              reject(err);
            },
          });
        }
      );
    });

  const confirmUpdateAttribute = (code: string) =>
    new Promise((resolve, reject) => {
      const user = UserPool.getCurrentUser()!;
      user.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err) {
            reject(err);
            return;
          }
          console.log("getCurrentUser: ", user);
          user.verifyAttribute("phone_number", code, {
            onSuccess: async () => {
              try {
                const attributes = await getUserAttributes(user);
                console.log("attributes : ", attributes);
                const pn = getPhoneNumberString(
                  attributes.phone_number.substring(3)
                );
                console.log("pn : ", pn);
                await apis.user.updatePhoneNumber(user.getUsername(), pn);
                resolve(undefined);
              } catch (e) {
                console.log("user db update email error : ", e);
                reject(e);
              }
            },
            onFailure: (err) => {
              reject(err);
            },
          });
        }
      );
    });

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "cognito",
        user: {
          displayName: state?.user?.name || "Minimals",
          role: "admin",
          ...state.user,
        },
        login,
        register,
        confirmRegister,
        resendRegisterCode,
        resetPassword,
        resetPasswordSubmit,
        refreshLoginProcess,
        updateAttribute,
        confirmUpdateAttribute,
        getAttributeVerificationCode,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
